import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {map, tap} from 'rxjs/operators';

export interface SourceResolved {
  readonly name?: string;
  readonly oName?: string;
  readonly oType?: string;
  readonly jPos?: string;
  readonly jRaDeg?: number;
  readonly jDecDeg?: number;
  readonly mType?: string;
  readonly resolver?: string;
  readonly radius?: number;
}

const defaultRadius = 10;
const resolverUrl = 'https://cdsweb.u-strasbg.fr/cgi-bin/nph-sesame/-ox?';

@Injectable({
  providedIn: 'root'
})
export class SourceResolverService {
  private readonly sourceResolvedByName = new Map<string, SourceResolved>();

  constructor(private readonly http: HttpClient) {
  }

  resolveSource(sourceNameValue: string): Observable<SourceResolved> {
    if (sourceNameValue?.length > 0) {
      const sourceNameValueTokens = sourceNameValue.split(',');
      const sourceName = sourceNameValueTokens[0];
      const radius = +sourceNameValueTokens[1] || defaultRadius;

      const isSourceCached = (this.getSourceResolved(sourceName) !== undefined);

      if (isSourceCached) {
        return of(this.getSourceResolved(sourceName)).pipe(
          map((sourceResolved) => ({...sourceResolved, radius}))
        )
      } else {
        return this.fetchSource(sourceName).pipe(
          map((sourceResolved) => ({...sourceResolved, radius}))
        );
      }
    } else {
      return of({});
    }
  }

  private fetchSource(name: string): Observable<any> {
    const url = `${resolverUrl}${name}`;

    return this.http.get(url, {responseType: 'text'}).pipe(
      map((response) => mapResponseToSourceResolved(response)),
      tap(sourceResolved => this.cacheSourceResolved(sourceResolved)),
      map(sourceResolved => sourceResolved)
    );
  }

  private getSourceResolved(name: string): SourceResolved {
    return this.sourceResolvedByName.get(name);
  }

  private cacheSourceResolved(sourceResolved: SourceResolved): void {
    const {name, oName} = sourceResolved;

    if (oName) {
      this.sourceResolvedByName.set(name, sourceResolved);
    }
  }
}

const mapResponseToSourceResolved = (xml: string): SourceResolved => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xml, 'text/xml');

  const name = xmlDoc.getElementsByTagName('name')[0]?.childNodes[0]?.nodeValue;
  const oName = xmlDoc.getElementsByTagName('oname')[0]?.childNodes[0]?.nodeValue;
  const oType = xmlDoc.getElementsByTagName('otype')[0]?.childNodes[0]?.nodeValue;
  const jPos = xmlDoc.getElementsByTagName('jpos')[0]?.childNodes[0]?.nodeValue;
  const jRaDeg = +xmlDoc.getElementsByTagName('jradeg')[0]?.childNodes[0]?.nodeValue;
  const jDecDeg = +xmlDoc.getElementsByTagName('jdedeg')[0]?.childNodes[0]?.nodeValue;
  const mType = xmlDoc.getElementsByTagName('MType')[0]?.childNodes[0]?.nodeValue;
  const resolver = xmlDoc.getElementsByTagName('Resolver')[0]?.attributes[0]?.nodeValue;

  return {name, oName, oType, jPos, jRaDeg, jDecDeg, mType, resolver};
}
